import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Card, Spinner } from "@/components/common";
import { WPScopeNote } from "./WPStatusBadge";
import { WPLogoPicker } from "./WPLogoField";
import { Field, PagesInput, LangToggle, checkCommon } from "./WPFields";
import { wpApi, LANGS, inputCls, btnPrimary, btnGhost, newKey, useWP } from "./wpApi";

const EMPTY = { company: "", website_url: "", goal: "", notes: "", languages: ["de"], page_scope: "10" };
const fromOrder = (o) => ({ company: o.company, website_url: o.website_url || "", goal: o.goal || "", notes: o.notes || "", languages: o.languages, page_scope: String(o.page_scope) });

function validate(f) {
  if (!f.company.trim()) return "Bitte den Firmennamen angeben.";
  if (f.website_url.trim() && (/\s/.test(f.website_url.trim()) || !f.website_url.includes("."))) return "Der WWW-Link ist ungültig (Beispiel: www.firma.ch).";
  return checkCommon(f);
}

export default function WPOrderForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const { t, err } = useWP();
  const [form, setForm] = useState(EMPTY);
  const [revision, setRevision] = useState(null);
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("");
  const [logo, setLogo] = useState(null);
  const [key] = useState(newKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const loadOrder = () => wpApi.get(id).then((r) => { setForm(fromOrder(r.data.order)); setRevision(r.data.order.revision); setError(null); setConflict(false); })
    .catch((e) => setError(err(e, "Auftrag nicht gefunden"))).finally(() => setLoading(false));
  useEffect(() => {
    if (isEdit) loadOrder();
    else wpApi.presets().then((r) => setPresets(r.data)).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const applyPreset = (pid) => {
    setPresetId(pid);
    const p = presets.find((x) => x.id === pid);
    if (p) setForm((f) => ({ ...f, languages: p.languages, page_scope: String(p.page_scope), goal: p.goal || f.goal, notes: p.notes || f.notes }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const v = validate(form);
    if (v) { setError(t(v)); setConflict(false); return; }
    setBusy(true); setError(null); setConflict(false);
    const body = { company: form.company, website_url: form.website_url || null, goal: form.goal || null, notes: form.notes || null,
      languages: LANGS.filter((l) => form.languages.includes(l)), page_scope: Number(form.page_scope) };
    try {
      if (isEdit) {
        await wpApi.update(id, { ...body, revision });
        toast.success(t("Änderungen gespeichert"));
        nav(`/admin/website-production/${id}`);
        return;
      }
      const { data } = await wpApi.create({ ...body, preset_id: presetId || null, idempotency_key: key });
      if (logo && !data.reused) {
        try { await wpApi.uploadLogo(data.id, logo, data.revision); } catch (ex) { toast.error(err(ex, "Logo wurde nicht gespeichert")); }
      }
      toast.success(t(data.reused ? "Auftrag war bereits gespeichert" : "Auftrag gespeichert"));
      nav(`/admin/website-production/${data.id}`);
    } catch (ex) {
      setError(err(ex, "Speichern fehlgeschlagen"));
      setConflict(isEdit && ex?.response?.status === 409);
      setBusy(false);
    }
  };

  if (loading) return <Spinner />;
  const back = isEdit ? `/admin/website-production/${id}` : "/admin/website-production";
  return (
    <div data-testid="wp-order-form-page" className="max-w-4xl">
      <Link to={back} className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-4 transition-colors" data-testid="wp-form-back"><ArrowLeft className="w-4 h-4" /> {t("Zurück")}</Link>
      <PageHeader title={t(isEdit ? "Auftrag bearbeiten" : "Neuer Auftrag")}
        subtitle={isEdit ? `${t("Version")} ${revision} · ${t("Änderungen werden im Verlauf festgehalten.")}` : t("Pflicht für vollständige Auftragsdaten: Firma, WWW-Link, Logo, Ziel, Sprachen, Seitenumfang.")} />
      <WPScopeNote />
      <form onSubmit={submit} noValidate data-testid="wp-order-form">
        <Card className="p-5 sm:p-7 grid md:grid-cols-2 gap-5">
          {!isEdit && presets.length > 0 && (
            <Field label={t("Vorlage anwenden")} hint={t("Übernimmt Sprachen, Seitenumfang, Ziel und Notizen als Kopie.")} className="md:col-span-2">
              <select value={presetId} onChange={(e) => applyPreset(e.target.value)} className={inputCls} data-testid="wp-preset-select">
                <option value="">{t("Keine Vorlage")}</option>
                {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          )}
          <Field label={t("Firmenname")} required><input value={form.company} onChange={set("company")} maxLength={200} className={inputCls} placeholder={t("Muster AG")} data-testid="wp-field-company" /></Field>
          <Field label={t("WWW-Link")} hint={t("Wird nur gespeichert, nicht abgerufen.")}><input value={form.website_url} onChange={set("website_url")} maxLength={500} className={inputCls} placeholder="www.firma.ch" data-testid="wp-field-website" /></Field>
          <Field label={t("Seitenumfang (1–100)")} required><PagesInput value={form.page_scope} onChange={set("page_scope")} /></Field>
          <Field label={t("Sprachen")} required hint={t("Eine oder mehrere Sprachen; gespeichert in der Reihenfolge DE, FR, IT, EN.")}><LangToggle value={form.languages} onChange={set("languages")} /></Field>
          <Field label={t("Primäres Kundenziel")} className="md:col-span-2"><textarea value={form.goal} onChange={set("goal")} maxLength={1000} rows={3} className={inputCls} placeholder={t("z. B. mehr Anfragen für Gartenpflege in der Region Zürich")} data-testid="wp-field-goal" /></Field>
          <Field label={t("Notizen")} className="md:col-span-2"><textarea value={form.notes} onChange={set("notes")} maxLength={2000} rows={3} className={inputCls} data-testid="wp-field-notes" /></Field>
          {isEdit ? <p className="md:col-span-2 text-xs text-gray-500">{t("Das Logo ersetzen Sie auf der Detailseite des Auftrags.")}</p>
            : <Field label={t("Original-Logo")} className="md:col-span-2"><WPLogoPicker file={logo} onChange={setLogo} /></Field>}
        </Card>
        {error && (
          <div role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex flex-wrap items-center gap-3" data-testid="wp-form-error">
            <span>{error}</span>
            {conflict && <button type="button" onClick={loadOrder} className="underline" data-testid="wp-form-reload">{t("Neu laden")}</button>}
          </div>
        )}
        <div className="mt-5 flex flex-wrap gap-3">
          <button type="submit" disabled={busy} className={btnPrimary} data-testid="wp-form-submit"><Save className="w-4 h-4" /> {t(busy ? "Wird gespeichert …" : "Speichern")}</button>
          <Link to={back} className={btnGhost} data-testid="wp-form-cancel">{t("Abbrechen")}</Link>
        </div>
      </form>
    </div>
  );
}
